import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ton`;

const TonLobby = ({ user, onGameStart }) => {
  const [queueSize, setQueueSize] = useState(0);
  const [matchedGameId, setMatchedGameId] = useState(null);
  const [dots, setDots] = useState('');
  const navigate = useNavigate();
  const pollingRef = useRef(null);
  const hasNavigated = useRef(false);
  const dotsRef = useRef(null);

  useEffect(() => {
    dotsRef.current = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(dotsRef.current);
  }, []);

  useEffect(() => {
    if (matchedGameId && !hasNavigated.current) {
      hasNavigated.current = true;
      toast.success('Partita trovata!');
      onGameStart(matchedGameId);
      navigate(`/ton/game/${matchedGameId}`);
    }
  }, [matchedGameId, navigate, onGameStart]);

  const checkMatchmaking = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/game/join-matchmaking`, { user_id: user.user_id });
      if (res.data.matched) setMatchedGameId(res.data.game_id);
      else setQueueSize(res.data.queue_size);
    } catch (e) { console.error(e); }
  }, [user.user_id]);

  useEffect(() => {
    checkMatchmaking();
    pollingRef.current = setInterval(checkMatchmaking, 2000);

    // Subscribe to queue changes
    const queueChannel = supabase.channel('ton_matchmaking_queue_updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ton_matchmaking_queue' },
        (payload) => {
          if (payload.eventType === 'DELETE' && payload.old.user_id === user.user_id) {
            checkMatchmaking();
          }
          checkMatchmaking();
        }
      ).subscribe();

    // Subscribe to game creation
    const gameChannel = supabase.channel('ton_game_sessions_creation')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ton_game_sessions' },
        (payload) => {
          const players = payload.new.players || [];
          if (players.some(p => p.user_id === user.user_id)) {
            setMatchedGameId(payload.new.id);
          }
        }
      ).subscribe();

    return () => {
      clearInterval(pollingRef.current);
      queueChannel.unsubscribe();
      supabase.removeChannel(queueChannel);
      gameChannel.unsubscribe();
      supabase.removeChannel(gameChannel);
    };
  }, [checkMatchmaking, user.user_id]);

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #030818 0%, #060f28 50%, #030818 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '20px 16px', position: 'relative', overflow: 'hidden',
    }}>
      {/* Background blobs */}
      <motion.div animate={{ scale:[1,1.2,1], opacity:[0.3,0.6,0.3] }} transition={{ duration:6, repeat:Infinity }}
        style={{ position:'absolute', top:'20%', left:'20%', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,0.3),transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />
      <motion.div animate={{ scale:[1.1,1,1.1], opacity:[0.2,0.5,0.2] }} transition={{ duration:8, repeat:Infinity, delay:2 }}
        style={{ position:'absolute', bottom:'20%', right:'20%', width:250, height:250, borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,0.3),transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />

      <button onClick={() => navigate('/games')} style={{
        position:'fixed', top:14, left:16, zIndex:50,
        background:'rgba(15,15,20,0.85)', backdropFilter:'blur(8px)',
        border:'1px solid rgba(59,130,246,0.3)', borderRadius:10,
        padding:'6px 14px', color:'#60a5fa', fontSize:13, fontWeight:700, cursor:'pointer',
      }}>
        ← Survival Games
      </button>

      <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
        style={{ position:'relative', zIndex:1, width:'100%', maxWidth:440 }}>

        <div style={{ textAlign:'center', marginBottom:8 }}>
          <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:2, repeat:Infinity }}
            style={{ fontSize:24, color:'#60a5fa', letterSpacing:12 }}>
            ✦ 10 ✦
          </motion.div>
        </div>

        <div style={{
          background:'linear-gradient(160deg,rgba(6,15,40,0.98),rgba(3,8,24,0.99))',
          borderRadius:24, border:'1px solid rgba(59,130,246,0.25)',
          boxShadow:'0 0 0 1px rgba(0,0,0,0.8), 0 24px 60px rgba(0,0,0,0.8), 0 0 60px rgba(59,130,246,0.05)',
          padding:'36px 32px', position:'relative', overflow:'hidden',
        }}>
          {/* Corners */}
          {[{top:12,left:12},{top:12,right:12},{bottom:12,left:12},{bottom:12,right:12}].map((pos,i) => (
            <div key={i} style={{ position:'absolute', ...pos, width:28, height:28,
              borderTop: pos.top!==undefined ? '2px solid rgba(59,130,246,0.3)' : undefined,
              borderBottom: pos.bottom!==undefined ? '2px solid rgba(59,130,246,0.3)' : undefined,
              borderLeft: pos.left!==undefined ? '2px solid rgba(59,130,246,0.3)' : undefined,
              borderRight: pos.right!==undefined ? '2px solid rgba(59,130,246,0.3)' : undefined,
            }} />
          ))}

          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:'0.2em', color:'rgba(96,165,250,0.5)', textTransform:'uppercase', marginBottom:10 }}>
              Ten or Nothing
            </div>
            <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:26, fontWeight:900, margin:'0 0 6px',
              background:'linear-gradient(to bottom,#fff,#93c5fd)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              Sala d'attesa
            </h1>
            <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.35)' }}>
              Cercando avversari{dots}
            </p>
          </div>

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(to right,transparent,rgba(59,130,246,0.25))' }} />
            <span style={{ color:'rgba(59,130,246,0.4)', fontSize:14 }}>✦</span>
            <div style={{ flex:1, height:1, background:'linear-gradient(to left,transparent,rgba(59,130,246,0.25))' }} />
          </div>

          {/* Player slots */}
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', textAlign:'center', marginBottom:14 }}>
              Giocatori in attesa
            </div>
            <div style={{ display:'flex', justifyContent:'center', gap:10 }}>
              {[...Array(4)].map((_,i) => (
                <motion.div key={i}
                  animate={i < queueSize ? { opacity:[0.7,1,0.7] } : {}}
                  transition={{ duration:1.5, repeat:Infinity }}
                  style={{
                    width:44, height:44, borderRadius:10,
                    background: i < queueSize ? 'linear-gradient(145deg,#3b82f6,#1d4ed8)' : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${i < queueSize ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: i < queueSize ? '0 0 16px rgba(59,130,246,0.5)' : 'none',
                    fontSize:20,
                  }}
                >
                  {i < queueSize ? '♟' : '·'}
                </motion.div>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:12 }}>
              <span style={{ fontFamily:"'Cinzel',serif", fontSize:32, fontWeight:900,
                background:'linear-gradient(to bottom,#fff,#3b82f6)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                {queueSize}
              </span>
              <span style={{ fontSize:18, color:'rgba(59,130,246,0.4)', fontWeight:700 }}> / 4</span>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height:3, borderRadius:2, background:'rgba(255,255,255,0.05)', overflow:'hidden' }}>
            <motion.div
              animate={{ x:['-100%','200%'] }}
              transition={{ duration:1.8, repeat:Infinity, ease:'easeInOut' }}
              style={{ height:'100%', width:'40%', background:'linear-gradient(to right,transparent,#3b82f6,transparent)', borderRadius:2 }}
            />
          </div>

          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:'rgba(255,255,255,0.25)' }}>
            Connesso come <span style={{ color:'#60a5fa', fontWeight:700 }}>{user.username}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TonLobby;
